import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  List,
  ListItem,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import QRANDROID from "../../../assets/images/onBoarding/QRANDROID.png";
import QRAPPLE from "../../../assets/images/onBoarding/QRAPPLE.png";
import FORANDROID from "../../../assets/images/onBoarding/FORANDROID.png";
import FORAPPLE from "../../../assets/images/onBoarding/FORAPPLE.png";
import DefaultUptimeTheme from "component/theme/defaultTheme";

const PRODUCTIVITY_CONSTANTS = {
  MOBILE: "Mobile",
  DESKTOP: "Desktop",
  NODATA: "NoData",
  FOR_ANDROID: "FOR-ANDROID",
  FOR_APPLE: "FOR-APPLE",
};

const SoftwareItem = ({ configInfo }: { configInfo: any }) => {
  const handleOnClick = () => {
    window.open(configInfo.url, "_blank");
  };

  return (
    <ListItem divider sx={{ padding: "0px" }}>
      <Card
        sx={{
          display: "flex",
          boxShadow: "none",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={handleOnClick}
      >
        <CardMedia
          component="img"
          image={configInfo?.pictures || ""}
          sx={{ width: "128px", backgroundSize: "contain" }}
          alt={configInfo?.name}
          title={configInfo?.name}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingLeft: "36px",
          }}
        >
          <Typography variant="overline">
            {configInfo?.brand?.name ? configInfo?.brand?.name : ""}
          </Typography>
          <Typography variant="h6">
            {configInfo?.name ? configInfo?.name : ""}
          </Typography>
        </Box>
      </Card>
    </ListItem>
  );
};
const MobileItem = ({ configInfo }: { configInfo: any }) => {
  const QRImage =
    configInfo?.sku === PRODUCTIVITY_CONSTANTS.FOR_ANDROID
      ? QRANDROID
      : QRAPPLE;
  const TitleImage =
    configInfo?.sku === PRODUCTIVITY_CONSTANTS.FOR_ANDROID
      ? FORANDROID
      : FORAPPLE;
  return (
    <ListItem sx={{ padding: "0px" }}>
      <Card
        sx={{
          display: "flex",
          boxShadow: "none",
          width: "100%",
          cursor: "pointer",
          flexDirection: "column",
          padding: "0 8px 0 8px",
        }}
      >
        <CardMedia
          component="img"
          image={TitleImage || ""}
          sx={{ backgroundSize: "contain", padding: "0 16px 0 16px" }}
          alt="Title"
          title="Title"
        />
        <CardMedia
          component="img"
          image={QRImage || ""}
          sx={{ backgroundSize: "contain" }}
          alt={configInfo?.name}
          title={configInfo?.name}
        />
      </Card>
    </ListItem>
  );
};

const TabContent = ({
  tabValue,
  productivityAppsConfig,
  locale,
}: {
  tabValue: string;
  productivityAppsConfig: any;
  locale:any
}) => {
  const DesktopConfig = productivityAppsConfig?.filter(
    (item: any) => item?.sku === PRODUCTIVITY_CONSTANTS.DESKTOP
  )[0];
  const MobileConfig = productivityAppsConfig?.filter(
    (item: any) => item?.sku === PRODUCTIVITY_CONSTANTS.MOBILE
  )[0];
  return (
    <Box paddingTop="8px">
      <Card sx={{ boxShadow: "none" }}>
        <List sx={{ padding: "0px" }}>
          <CardContent
            sx={{
              maxHeight:
                tabValue === PRODUCTIVITY_CONSTANTS.MOBILE
                  ? "max-content"
                  : "300px",
              padding: "0px !important",
              overflow: "auto",
              paddingLeft: "16px",
              display:
                tabValue === PRODUCTIVITY_CONSTANTS.MOBILE ? "flex" : "block",
              flexDirection:
                tabValue === PRODUCTIVITY_CONSTANTS.MOBILE ? "row" : "column",
              alignItems: "flex-start",
            }}
          >
            {tabValue === PRODUCTIVITY_CONSTANTS.DESKTOP &&
            DesktopConfig?.value?.length > 0 ? (
              DesktopConfig?.value?.map((config: any) => (
                <Fragment key={config?.sku}>
                  <SoftwareItem configInfo={config} />
                </Fragment>
              ))
            ) : tabValue === PRODUCTIVITY_CONSTANTS.MOBILE &&
              MobileConfig?.value?.length > 0 ? (
              MobileConfig?.value?.map((config: any) => (
                <Fragment key={config?.sku}>
                  <MobileItem configInfo={config} />
                </Fragment>
              ))
            ) : (
              <Typography variant="subtitle2" paddingLeft="24px">
                {locale?.noData}
              </Typography>
            )}
          </CardContent>
        </List>
      </Card>
    </Box>
  );
};
export interface IProductivitySectionProps {
  productivityAppsConfig: any[];
  locale?: any;
}

const ProductivitySection = ({
  productivityAppsConfig,
  locale
}: IProductivitySectionProps) => {
  const theme = useTheme() as typeof DefaultUptimeTheme;

  const [tabValue, setTabValue] = useState(PRODUCTIVITY_CONSTANTS.NODATA);

  const tabList: { sku?: string; name?: string }[] = useMemo(
    () =>
      productivityAppsConfig?.length > 0
        ? productivityAppsConfig?.map((item: any) => ({
            sku: item?.sku,
            name: item?.name,
          }))
        : [
            {
              sku: PRODUCTIVITY_CONSTANTS.NODATA,
              name: locale?.noData,
            },
          ],
    [productivityAppsConfig]
  );

  const handleTabChange = (
    event: React.SyntheticEvent,
    newTabValue: string
  ) => {
    setTabValue(newTabValue);
  };

  useEffect(() => {
    setTabValue(tabList[0]?.sku || locale?.noData);
  }, [tabList]);

  return (
      <Card
        sx={{
          borderRadius: "4px",
          boxShadow: `0px 0px 0px 1px ${theme.uptimeColors.grayScales.boxShadow}`,
          minHeight: "393px",
        }}
      >
        <CardHeader
          sx={{ padding: "10px 16px 0px 16px" }}
          title={
            <Typography variant="h6" color="text.primary">
              {locale?.productivityApps?.title}
            </Typography>
          }
        />
        <CardContent sx={{ padding: "0 0 0 8px !important" }}>
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="inherit"
            >
              {tabList?.length > 0 &&
                tabList?.map((tab: any) => (
                  <Tab label={tab?.name ? tab?.name : locale?.productivityApps?.[tab?.sku]} value={tab?.sku} />
                ))}
              {tabList?.length === 0 && (
                <Tab
                  label={locale?.productivityApps?.noData}
                  value={PRODUCTIVITY_CONSTANTS.NODATA}
                />
              )}
            </Tabs>
          </Box>
          <TabContent
            tabValue={tabValue}
            productivityAppsConfig={productivityAppsConfig}
            locale={locale}
          />
        </CardContent>
      </Card>
  );
};

export default ProductivitySection;